<template>
  <dd
    @mouseenter="hoverItem"
    @mouseleave="leaveItem"
    @click.stop="selectOptionClick"
    @keydown.enter="selectOptionClick"
    class="zm-select-dropdown__item"
    v-if="visible"
    :id="optionId"
    role="option"
    :aria-selected="hover"
    :aria-label="ariaLabel || currentLabel"
    ref="optionLi"
    :tabindex="computedDisabled ? -1 : 0"
    :class="[{
        'selected': itemSelected,
        'hover': hover,
         'is-disabled': computedDisabled || groupDisabled || limitReached
      },
      optionSize ? 'zm-select-dropdown__item--' + optionSize : '']">
    <slot>
      <span ref="realLabelSpan" class="real-label-span">{{ currentLabel }}</span>
      <span v-if="!widthLimit">{{ currentLabel }}</span>
      <div v-else>
        <zm-tooltip
          v-if="hasTips"
          ref="tooltips"
          popper-class="zm-select-dropdown__item__tooltips"
          placement="right"
          :manual="true"
        >
          <div slot="content" class="zm-select-dropdown__item__tooltips__content" v-text="currentLabel"></div>
          <span class="zm-select-dropdown__label_span">{{ currentLabel }}</span>
        </zm-tooltip>
        <span v-else class="zm-select-dropdown__label_span">{{ currentLabel }}</span>
      </div>
    </slot>
  </dd>
</template>

<script type="text/babel">
  import ZmTooltip from '../tooltip';
  import ZmCheckbox from '../checkbox'
  import Emitter from '../../mixins/emitter';
  import { getValueByPath, escapeRegexpString } from '../../utils/util';

  export default {
    mixins: [Emitter],

    components: {
      ZmTooltip,
      ZmCheckbox
    },

    name: 'ZmOption',

    componentName: 'ZmOption',

    inject: ['select'],

    props: {
      value: {
        required: true
      },
      label: [String, Number],
      ariaLabel: String,
      created: Boolean,
      disabled: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        groupDisabled: false,
        hover: false,
        visible: true,
        hitState: false,
        isHover: false,
        hasTips: false,
        widthLimit: this.select.widthLimit
      };
    },

    computed: {
      optionId() {
        return 'select-item-' + this.selectId + '-' + this.itemIndex;
      },
      isObject() {
        return Object.prototype.toString.call(this.value).toLowerCase() === '[object object]';
      },

      currentLabel() {
        return this.label || (this.isObject ? '' : this.value);
      },

      currentValue() {
        return this.value || this.label || '';
      },

      itemSelected() {
        if (!this.select.multiple) {
          return this.isEqual(this.value, this.select.value);
        } else {
          return this.contains(this.select.value, this.value);
        }
      },

      limitReached() {
        if (this.select.multiple) {
          return !this.itemSelected &&
            (this.select.value || []).length >= this.select.multipleLimit &&
            this.select.multipleLimit > 0;
        } else {
          return false;
        }
      },
      selectId() {
        return this.select._targetId;
      },
      itemIndex() {
        return this.select.options.indexOf(this);
      },
      multiSelect() {
        return this.select.localMultiple
      },
      optionSize() {
        return this.select.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
      },
      computedDisabled() {
        if (this.select.multiple && (this.select.remote || this.select.filterable)) {
          return this.disabled || this.itemSelected
        } else {
          return this.disabled
        }
      }
    },

    watch: {
      hover(newVal) {
        // hover from options
        // changed by key up | down
        this.isHover = newVal;
      },
      isHover(newVal) {
        if (!this.widthLimit || !this.hasTips) {
          return;
        }
        if (this.widthLimit && this.widthLimit.disableTip) {
          return;
        }
        if (newVal) {
          this.$refs.tooltips && (this.$refs.tooltips.showPopper = true);
        } else {
          this.$refs.tooltips && (this.$refs.tooltips.showPopper = false);
        }

      },
      'select.visible'(newVal) {
        if (!this.widthLimit) {
          return;
        }
        if (newVal) {
          this.checkShowTips();
        } else {
          this.isHover = false;
        }
      },
      currentLabel() {
        if (!this.created && !this.select.remote) this.dispatch('ZmSelect', 'setSelected');
      },
      value(val, oldVal) {
        const { remote, valueKey } = this.select;
        if (!this.created && !remote) {
          if (valueKey && typeof val === 'object' && typeof oldVal === 'object' && val[valueKey] === oldVal[valueKey]) {
            return;
          }
          this.dispatch('ZmSelect', 'setSelected');
        }
      }
    },

    methods: {
      isEqual(a, b) {
        if (!this.isObject) {
          return a === b;
        } else {
          const valueKey = this.select.valueKey;
          return getValueByPath(a, valueKey) === getValueByPath(b, valueKey);
        }
      },

      contains(arr = [], target) {
        if (!this.isObject) {
          return arr.indexOf(target) > -1;
        } else {
          const valueKey = this.select.valueKey;
          return arr.some(item => {
            return getValueByPath(item, valueKey) === getValueByPath(target, valueKey);
          });
        }
      },

      handleGroupDisabled(val) {
        this.groupDisabled = val;
      },
      checkShowTips() {
        if (!this.widthLimit) {
          return;
        }
        const oWidth = this.$refs.optionLi && this.$refs.optionLi.getBoundingClientRect().width;
        const iWidth = this.$refs.realLabelSpan && this.$refs.realLabelSpan.getBoundingClientRect().width;
        if (iWidth + 24 > oWidth) {
          this.hasTips = true;
        } else {
          this.hasTips = false;
        }
      },
      hoverItem() {
        this.isHover = true;
        if (!this.computedDisabled && !this.groupDisabled) {
          this.select.hoverIndex = this.select.options.indexOf(this);
        }
      },
      leaveItem() {
        this.isHover = false;
      },

      selectOptionClick() {
        if (this.multiSelect) {
          return;
        }
        if (this.computedDisabled !== true && this.groupDisabled !== true) {
          this.dispatch('ZmSelect', 'handleOptionClick', [this, true]);
        }
      },

      queryChange(query) {
        this.visible = new RegExp(escapeRegexpString(query), 'i').test(this.currentLabel) || this.created;
        if (!this.visible) {
          this.select.filteredOptionsCount--;
        }
      }
    },

    created() {
      this.select.options.push(this);
      this.select.cachedOptions.push(this);
      this.select.optionsCount++;
      this.select.filteredOptionsCount++;

      this.$on('queryChange', this.queryChange);
      this.$on('handleGroupDisabled', this.handleGroupDisabled);
    },
    beforeDestroy() {
      this.$refs.tooltips && (this.$refs.tooltips.showPopper = false);
      this.select.onOptionDestroy(this.select.options.indexOf(this));
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(select-dropdown) {
    @include e(item) {
      font-size: inherit;
      padding: 8px $--input-default-padding-horizontal;
      position: relative;

      color: $--select-option-color;
      line-height: $--select-option-height - 16;
      box-sizing: border-box;
      cursor: pointer;
      overflow: hidden;
      @include when(disabled) {
        color: $--select-option-disabled-color;
        cursor: not-allowed;

        &:hover {
          background-color: $--color-white;
          color: $--select-option-disabled-color;
        }
      }

      &.hover, &:hover, &:focus {
        outline: 0;
        background-color: $--select-option-hover-background;
        color: $--select-option-selected;
      }
      &:active {
        background-color: $--background-color-pressed;
      }

      &.selected {
        color: $--select-option-selected;
      }
      .real-label-span {
        opacity: 0;
        position: absolute;
        left: 0;
        top: 0;
        z-index: -1;
        white-space: nowrap;
      }
      @include m(large) {
        line-height: $--select-option-lg-height - 16;
      }
    }

  }

  .zm-select-dropdown__item__tooltips__content {
    max-width: 240px;
    // word-break: break-word;
    word-wrap: break-word;
    overflow: hidden;
  }

  .zm-select-dropdown__item__tooltips {
    &[x-placement^="right"] {
      margin-left: #{$--tooltip-arrow-size + 16px} !important;
    }
  }

  .zm-select-menu-width-limit {
    .zm-select-dropdown__label_span {
      display: block;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
</style>
